import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Writing modular Backbone with Backbone.SubRoute",
  "description": "",
  "published": "2013-07-31T00:00:00.000Z",
  "redirect_from": ["/blog/writing-modular-backbone-with-backbone-subroute/swizec/6297"],
  "categories": "JavaScript, jQuery, Modules, Programming, Router, Uncategorized, Web application",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Working on a complicated frontend web app, Backbone soon becomes unwieldy. Routes definitions grow and grow, views steal each other's actions and parts of the code try to become independent modules.`}</p>
    <p>{`This is the problem I faced a few weeks ago while working on the fanciest signup funnel I ever helped create. Visitors would see the splash page, create their cart, revise it, sign up, add billing and shipping info, and finally checkout without ever refreshing the page.`}</p>
    <p>{`A daunting challenge only made worse by the complexity of components involved. We needed a full-blown inventory listing, a full-blown cart editor and we even added some shiny bits to the billing and shipping forms. The funnel changes into an accordion after the first step.`}</p>
    <p>{`Oh and the user can refresh at any point in the funnel without losing their place.`}</p>
    <p>{`To divide work among the team, we took a straight-forward modular approach: a module handles the whole funnel, submodules take care of each step. This way everyone can focus on their own piece of the funnel and the javascript code can be reused when making the standalone versions of each step for signed-in users.`}</p>
    <h2 {...{
      "id": "the-skeleton"
    }}>{`The skeleton`}</h2>
    <p><a parentName="p" {...{
        "href": "http://geekdave.com/2012/04/05/module-specific-subroutes-in-backbone/"
      }}>{`Backbone.SubRoute`}</a>{` makes this approach possible by letting you create Backbone routers attached at a certain path in the URL. By doing something like `}<inlineCode parentName="p">{`var bla = new SubBla("/bla", …)`}</inlineCode>{` you create a self-contained Backbone app that handles everything under `}<inlineCode parentName="p">{`/bla`}</inlineCode>{` and doesn't care about the outside world.`}</p>
    <p>{`We didn't like how the official docs recommend going about this, so we made it automagical-er.`}</p>
    <p>{`A simple `}<inlineCode parentName="p">{`app_skeleton.js`}</inlineCode>{` file holds the entire application together and loads modules when they're needed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var YourApp = (window.YourApp = { Routers: {} });

YourApp.Router = Backbone.Router.extend({
  routes: {
    "": "invokeRootModule",
    ":module(/*subroute)": "invokeModule",
  },

  invokeRootModule: function () {
    this.invokeModule("root");
  },

  invokeModule: function (module, subroute) {
    module = module.toLowerCase().capitalize();

    if (!YourApp.Routers[module]) {
      YourApp.Routers[module] = new YourApp[
        module
      ].Router(module.toLowerCase() + "/", { createTrailingSlashRoutes: true });
    }
  },
});
`}</code></pre>
    <p>{`We agree to register all our modules as `}<inlineCode parentName="p">{`window.YourApp.Module.Router`}</inlineCode>{`. This lets us put every module in its own file and creates a predictable interface for creating instances of sub routers.`}</p>
    <p>{`That was easy, now all we need is a submodule.`}</p>
    <h2 {...{
      "id": "a-module"
    }}>{`A module`}</h2>
    <p>{`Modules work the same as standalone Backbone applications. They have views and collections and models and everything is held together by a router. You just have to conform to what app skeleton expects.`}</p>
    <p>{`A simple module might look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`(function ($) {
  YourApp.Root = {};

  YourApp.Root.Router = Backbone.SubRoute.extend({
    routes: {
      "(:step)": "step",
    },

    initialize: function () {
      // do stuff
    },

    step: function () {
      // something for this step
    },
  });
})(jQuery);
`}</code></pre>
    <p><inlineCode parentName="p">{`Router`}</inlineCode>{` is the only necessary part. Everything else is added to taste. We rely on the router's initialize function as the entry point into the module, so all initialization should happen in there.`}</p>
    <h2 {...{
      "id": "sub-sub-modules"
    }}>{`Sub sub modules`}</h2>
    <p>{`Let's make things interesting; the module is called `}<inlineCode parentName="p">{`Root`}</inlineCode>{` because it's going to handle submodules of its own - funnel steps in our case. `}<inlineCode parentName="p">{`Root`}</inlineCode>{` loads them on the home page, `}<inlineCode parentName="p">{`app_skeleton`}</inlineCode>{` loads them on standalone pages.`}</p>
    <p>{`For some reason dynamically instantiating submodules was not an option in this case. Not sure why, but it probably had to do with listening to events between submodules. Yes, unclean, but there's no reason to go overboard with architecture principles. Sometimes you just have to get shit done.`}</p>
    <p>{`Root's initialize becomes something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`        initialize: function () {
            this.subrouters = {};

            this.subrouters.step1 = new YourApp.Step1.Router("base/step1/");
            this.subrouters.step2 = new YourApp.Step2.Router("base/step2/");
            // ...

            this.__listenToSubrouters();

            // other stuff
        },
`}</code></pre>
    <p>{`Every subrouter is instantiated on initialize. If we want to change our views when particular submodules are active, we have to listen to their `}<inlineCode parentName="p">{`route`}</inlineCode>{` events because they steal routing from their parent. For instance, we had to make sure the accordion was visible, splash step was hidden and so on.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`        __listenToSubrouters: function () {
            _.keys(this.subrouters).map(_.bind(function (router) {
                this.listenTo(this.subrouters[router],
                              "route",
                              _.bind(function () {
                                  this.step(router, "",
                                                 {dont_navigate: true});
                              }, this));
            }, this));
        },
`}</code></pre>
    <p><inlineCode parentName="p">{`__listenToSubrouters`}</inlineCode>{` goes through all the instantiated routers and makes sure to call the `}<inlineCode parentName="p">{`step`}</inlineCode>{` function when a submodule does something.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`step`}</inlineCode>{` function becomes something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`        step: function(step, subpath, options) {
            // do view stuff here

            subpath = subpath || "";

            if (this.subrouters[step] && !(options && options.dont_navigate)) {
                this.subrouters[step].navigate(subpath, {trigger: true});
            }
        }
`}</code></pre>
    <p>{`This makes sure to `}<inlineCode parentName="p">{`.navigate`}</inlineCode>{` subrouters when it's appropriate and avoid doing so when the code is reacting to a subrouter's `}<inlineCode parentName="p">{`route`}</inlineCode>{` event. I've left out the view stuff because that isn't very interesting.`}</p>
    <h2 {...{
      "id": "win"
    }}>{`Win`}</h2>
    <p>{`And there you go. Backbone with a modular design that keeps modules neat, encapsulated and reusable.`}</p>
    <p>{`To make things even smoother I also added an event that triggers only when a certain step is fully visible. This helps submodules activate/deactivate their views properly because things like `}<a parentName="p" {...{
        "href": "http://imakewebthings.com/jquery-waypoints/"
      }}>{`jQuery Waypoints`}</a>{` don't initialize well when elements aren't at their final positions.`}</p>
    <p>{`As for the super fancy signup process - you'll be able to see it when `}<a parentName="p" {...{
        "href": "http://www.dwellers.com/"
      }}>{`Dwellers`}</a>{` launches. It's pretty great.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=80eafc50-068a-4f40-acaf-a20b7ce2ed15",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      